import { useMemo, useState } from "react";
import { showNotification } from "../../../../store/Central/selectors";
import { useRemindersStore } from "../../../../store/Reminders";
import { InvoiceReminderActions } from "../../types/invoice-table.types";
import { CreateRemindersDialog } from "../components/create-reminders-dialog.component";
import { InvoiceReminderHistoryDialog } from "../components/invoice-reminder-history-dialog.component";
import { PauseRemindersDialog } from "../components/pause-reminders-dialog.component";
import {
	PaymentReminderCandidate,
	PaymentReminderHistoryRow,
	ReminderInvoiceRef,
} from "../payment-reminder.types";
import { useReminderShare } from "./useReminderShare";

// Reminder entry points for Offene Posten and the Mahnwesen tables; dialogs render outside
// the context menu because the menu unmounts as soon as an item is clicked.
export const useInvoiceReminderActions = (onChanged: () => void) => {
	const [candidate, setCandidate] = useState<PaymentReminderCandidate | null>(
		null
	);
	const [pauseInvoiceId, setPauseInvoiceId] = useState<number | null>(null);
	const [historyTarget, setHistoryTarget] =
		useState<ReminderInvoiceRef | null>(null);
	const [historyRows, setHistoryRows] = useState<PaymentReminderHistoryRow[]>(
		[]
	);

	const { toggleReminderShare, shareEmailDialog } =
		useReminderShare(onChanged);

	const loadHistory = async (target: ReminderInvoiceRef) => {
		setHistoryRows(
			await useRemindersStore
				.getState()
				.fetchInvoiceHistory(target.invoiceId)
		);
	};

	const reminderActions = useMemo<InvoiceReminderActions>(
		() => ({
			onCreateReminder: async (invoice) => {
				const manualCandidate = await useRemindersStore
					.getState()
					.fetchManualCandidate(invoice.invoiceId);
				if (!manualCandidate) {
					showNotification({
						message:
							"Diese Rechnung kann nicht gemahnt werden (Mahnstopp oder maximale Mahnstufe erreicht).",
						type: "warning",
					});
					return;
				}
				setCandidate(manualCandidate);
			},
			onPauseReminders: (invoice) => setPauseInvoiceId(invoice.invoiceId),
			onResumeReminders: async (invoice) => {
				const resumed = await useRemindersStore
					.getState()
					.resumeReminders([invoice.invoiceId]);
				if (resumed) onChanged();
			},
			onShowReminderHistory: async (invoice) => {
				await loadHistory(invoice);
				setHistoryTarget(invoice);
			},
			onToggleReminderShare: async (invoice) => {
				const currentStage = (
					await useRemindersStore
						.getState()
						.fetchInvoiceHistory(invoice.invoiceId)
				).find((row) => row.is_latest);
				if (currentStage) await toggleReminderShare(currentStage);
			},
		}),
		[onChanged, toggleReminderShare]
	);

	const reminderDialogs = (
		<>
			<CreateRemindersDialog
				open={candidate !== null}
				candidates={candidate ? [candidate] : []}
				ignoreSchedule
				onClose={() => setCandidate(null)}
				onCompleted={onChanged}
			/>
			<PauseRemindersDialog
				open={pauseInvoiceId !== null}
				invoiceIds={pauseInvoiceId ? [pauseInvoiceId] : []}
				onClose={() => setPauseInvoiceId(null)}
				onCompleted={onChanged}
			/>
			<InvoiceReminderHistoryDialog
				invoice={historyTarget}
				rows={historyRows}
				onClose={() => setHistoryTarget(null)}
				onChanged={() => {
					if (historyTarget) loadHistory(historyTarget);
					onChanged();
				}}
			/>
			{shareEmailDialog}
		</>
	);

	return { reminderActions, reminderDialogs };
};
